import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BellIcon, 
  CheckIcon, 
  EyeIcon, 
  HeartIcon, 
  ChatBubbleLeftIcon, 
  UserPlusIcon, 
  CameraIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarDaysIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  UserIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import { formatDistanceToNow } from 'date-fns';
import { formatShortTime } from '../utils/dateUtils';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { 
    fetchNotifications, 
    markAsRead, 
    markAllAsRead, 
    respondToCollaborationInvite,
    fetchUnreadCount,
    clearError 
  } from '../store/slices/notificationsSlice';
import Button from '../components/common/Button';
import Switch from '../components/common/Switch';
import CollaborationShowcaseModal from '../components/collaboration/CollaborationShowcaseModal';
import Avatar from '../components/common/Avatar';
import { Notification, CollaborationInviteData, User, Collaboration, CollaborationCollaborator } from '../types';
import { crmAPI } from '../services/api';
import toast from 'react-hot-toast';

const NotificationsPage: React.FC = () => {
    const dispatch = useAppDispatch();

  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [reviewingCollaboration, setReviewingCollaboration] = useState<Notification | null>(null);
  const [responding, setResponding] = useState(false);
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const navigate = useNavigate();

  const [pendingLeadRequest, setPendingLeadRequest] = useState<any | null>(null);
  const [loadingLead, setLoadingLead] = useState(false);

  useEffect(() => {
    const checkPushStatus = async () => {
      const { pushNotificationService } = await import('../services/pushNotificationService');
      const subscribed = await pushNotificationService.isSubscribed();
      setIsPushEnabled(subscribed);
    };
    checkPushStatus();
  }, []);
    const { user: currentUser } = useAppSelector((state) => state.auth);
  const { 
    notifications, 
    loading, 
    unreadCount, 
    error,
    hasMore 
  } = useAppSelector((state) => state.notifications);
  
  useEffect(() => {
    // console.log("gee")
    dispatch(fetchNotifications({ limit: 20, offset: 0 }));
  }, [dispatch]);
  
  useEffect(() => {
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  const handleConfirmLead = async (leadId: string, notificationId: string) => {
    try {
      await crmAPI.confirmPortalLead(leadId);
      toast.success('Inquiry confirmed successfully!');
      setPendingLeadRequest(null);
      dispatch(markAsRead(notificationId));
      dispatch(fetchNotifications({ limit: 20, offset: 0 }));
    } catch (err) {
      toast.error('Failed to confirm request');
    }
  };

  const handleDeclineLead = async (leadId: string, notificationId: string) => {
    try {
      await crmAPI.declinePortalLead(leadId);
      toast.success('Inquiry declined.');
      setPendingLeadRequest(null);
      dispatch(markAsRead(notificationId));
      dispatch(fetchNotifications({ limit: 20, offset: 0 }));
    } catch (err) {
      toast.error('Failed to decline request');
    }
  };
 
  // Deduplicate notifications first
  const deduplicatedNotifications = React.useMemo(() => {
    const opportunityUpdates = new Map<string, Notification>();
    const otherNotifications: Notification[] = [];

    notifications.forEach(notification => {
      const isOpportunityUpdate = notification.title.startsWith('Opportunity Updated:');
      
      if (isOpportunityUpdate && notification.reference_id) {
        const existing = opportunityUpdates.get(notification.reference_id);
        if (!existing || new Date(notification.created_at) > new Date(existing.created_at)) {
          opportunityUpdates.set(notification.reference_id, notification);
        }
      } else {
        otherNotifications.push(notification);
      }
    });

    const combined = [...otherNotifications, ...Array.from(opportunityUpdates.values())];
    return combined.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [notifications]);

  // Calculate visible unread count based on deduplicated list
  const visibleUnreadCount = React.useMemo(() => {
    return deduplicatedNotifications.filter(n => !n.is_read).length;
  }, [deduplicatedNotifications]);

  const filteredNotifications = React.useMemo(() => {
    return filter === 'unread' 
      ? deduplicatedNotifications.filter(n => !n.is_read) 
      : deduplicatedNotifications;
  }, [deduplicatedNotifications, filter]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'like': return HeartIcon;
      case 'comment': return ChatBubbleLeftIcon;
      case 'message': return ChatBubbleLeftIcon;
      case 'application': return UserPlusIcon;
      case 'booking': return CameraIcon;
      case 'collaboration_invite': return UserPlusIcon;
      case 'collaboration_accept': return CheckCircleIcon;
      case 'collaboration_reject': return XCircleIcon;
      default: return BellIcon;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'like': return 'text-red-500 bg-red-100 dark:bg-red-900/20';
      case 'comment': return 'text-blue-500 bg-blue-100 dark:bg-blue-900/20';
      case 'message': return 'text-green-500 bg-green-100 dark:bg-green-900/20';
      case 'application': return 'text-purple-500 bg-purple-100 dark:bg-purple-900/20';
      case 'booking': return 'text-orange-500 bg-orange-100 dark:bg-orange-900/20';
      case 'collaboration_invite': return 'text-teal-500 bg-teal-100 dark:bg-teal-900/20';
      case 'collaboration_accept': return 'text-green-500 bg-green-100 dark:bg-green-900/20';
      case 'collaboration_reject': return 'text-red-500 bg-red-100 dark:bg-red-900/20';
      default: return 'text-gray-500 bg-gray-100 dark:bg-gray-800';
    }
  };

  

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      // Mark as read first
    await dispatch(markAsRead(notification.id)).unwrap();
      // await dispatch(markAsRead(notification.id));
    }

    // Handle collaboration invitations differently
    if (notification.type === 'collaboration_invite') {
      setReviewingCollaboration(notification);
      return;
    }

    if (notification.action_type === 'crm_lead_confirm') {
      if (!notification.reference_id) {
        toast.error('Invalid notification metadata');
        return;
      }
      setLoadingLead(true);
      try {
        const res = await crmAPI.getPortalLead(notification.reference_id);
        setPendingLeadRequest({
          id: notification.reference_id,
          notificationId: notification.id,
          ...res.data.data
        });
      } catch (err) {
        toast.error('Failed to load lead details');
        console.error(err);
      } finally {
        setLoadingLead(false);
      }
      return;
    }

    // Navigate based on deep link or action type
    // if (notification.deep_link) {
    //   const url = new URL(notification.deep_link);
    //   const path = url.pathname.replace('/app/', '/');
    //   navigate(path);
    // } else {
    //     // console.log("ccc", notification.action_type)
    //   switch (notification.action_type) {
    //     case 'view_profile':
    //       navigate(`/profile/${notification.reference_id}`);
    //       break;
    //     case 'view_opportunity':
    //       navigate(`/opportunity/${notification.reference_id}`);
    //       break;
    //     case 'open_chat':
    //       navigate(`/chat/${notification.reference_id}`);
    //       break;
    //     case 'view_collaboration':
    //       navigate(`/collaborations/${notification.reference_id}`);
    //       break;
    //     default:
    //       break;
    //   }
    // }
    // Handle different notification types
    // console.log(notification.action_type)
    switch (notification.action_type) {
      case 'open_chat':
      case 'new_message':
        // Instead of navigating to /chat/:conversationId, navigate to messages page
        // with state to open specific conversation
        navigate('/messages', {
          state: {
            openConversationId: notification.reference_id, // This should be conversation_id
            fromNotification: true
          }
        });
        break;
        
      case 'crm_lead_accepted':
      case 'crm_lead_declined':
        navigate('/crm');
        break;
        
      case 'crm_lead_converted':
        navigate('/crm', { state: { activeTab: 'bookings' } });
        break;
        
      case 'view_profile':
        navigate(`/profile/${notification.reference_id}`);
        break;

      case 'view_own_profile':
        navigate(`/profile/${currentUser?.id}/reviews/${notification.reference_id}`);
        break;
        
      case 'view_collaboration':
        navigate(`/profile/${currentUser?.id}/projects/${notification.reference_id}`);
        break;
        
      case 'view_opportunity':
        navigate(`/opportunities/${notification.reference_id}`);
        break;
        
      case 'new_rating':
        if (currentUser?.id) {
          navigate(`/profile/${currentUser.id}/reviews/${notification.reference_id}`);
        } else {
          console.error("User not found for navigation");
        }
        break;

      case 'view_application':
        if (notification.deep_link) {
          // Extract opportunity ID from deep link
          // Format: clickcollab://app/opportunities/{opportunityId}/applications
          const match = notification.deep_link.match(/opportunities\/([a-f0-9-]+)\/applications/);
          if (match && match[1]) {
            navigate(`/opportunities/${match[1]}/applications`, {
              state: {
                selectedApplicationId: notification.reference_id
              }
            });
          } else {
             // Fallback if regex fails (shouldn't happen if backend is consistent)
             console.warn("Could not extract opportunity ID from deep link:", notification.deep_link);
             // Try to navigate to generic opportunities page or show error
             navigate('/opportunities/my');
          }
        }
        break;

      case 'collab_request':
      case 'view_request':
        navigate('/collaboration-requests', {
          state: {
            selectedRequestId: notification.reference_id
          }
        });
        break;

      default:
        if (notification.deep_link) {
          // Handle deep links
          const url = notification.deep_link.replace('clickcollab://app', '');
          navigate(url);
        }
        break;
    }
  };



  const mapInviteToCollaboration = (notification: Notification): Collaboration => {
    const inviteData = notification.data as CollaborationInviteData;
    
    // Create a mock collaborator for the current user based on the invite role
    const currentUserName = currentUser 
      ? `${currentUser.first_name} ${currentUser.last_name}`
      : 'You';

    const currentUserCollaborator: CollaborationCollaborator = {
      id: 'current-user-invite',
      collaboration_id: inviteData.collaboration_id,
      name: currentUserName,
      user_id: currentUser?.id,
      role: inviteData.role,
      is_portal_user: true,
      approval_status: 'pending',
      joined_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user: currentUser || undefined
    };

    // Create a mock collaborator for the creator
    const creatorCollaborator: CollaborationCollaborator = {
      id: 'creator-invite',
      collaboration_id: inviteData.collaboration_id,
      name: inviteData.creator_name,
      user_id: notification.data?.sender_id, // Use sender_id from notification data
      role: inviteData.creator_role,
      is_portal_user: true,
      approval_status: 'approved', // Creator is approved
      joined_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      profile_pic: inviteData.creator_profile_pic
    };

    // Filter out creator and current user from the list to avoid duplicates
    const otherCollaborators = (inviteData.collaborators || []).filter(c => {
        const cId = c.id;
        const cUserId = (c as any).user_id;
        const cName = (c as any).name || 
                      ((c as any).user?.name) || 
                      ((c as any).user ? `${(c as any).user.first_name} ${(c as any).user.last_name}` : null) ||
                      `${c.first_name || ''} ${c.last_name || ''}`.trim();

        // Check if this collaborator is the current user
        if (currentUser) {
            if (cId === currentUser.id) return false;
            if (cUserId === currentUser.id) return false;
            
            const currentUserName = `${currentUser.first_name} ${currentUser.last_name}`;
            if (cName === currentUserName) return false;
        }
        
        // Check if this collaborator is the creator
        if (cName === inviteData.creator_name || 
            `${c.first_name} ${c.last_name}` === inviteData.creator_name) return false;
            
        return true;
    });

    return {
      id: inviteData.collaboration_id,
      creator_id: notification.data?.sender_id || 'creator-id-placeholder', 
      title: inviteData.title,
      project_description: inviteData.project_description,
      place: inviteData.place,
      is_paid: inviteData.is_paid,
      budget: inviteData.budget || 0,
      duration: inviteData.duration,
      start_date: inviteData.start_date,
      end_date: inviteData.end_date,
      tags: inviteData.tags || [],
      creator_role: inviteData.creator_role,
      creator_insights: inviteData.creator_insights || '',
      cover_image: undefined, // User requested to remove cover photo from the list
      images: inviteData.images || [],
      status: 'open', // Default
      is_public: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      collaborators: [currentUserCollaborator, creatorCollaborator, ...otherCollaborators.map(c => ({
          id: c.id,
          collaboration_id: inviteData.collaboration_id,
          name: (c as any).name || 
                ((c as any).user?.name) || 
                ((c as any).user ? `${(c as any).user.first_name} ${(c as any).user.last_name}` : null) ||
                `${c.first_name || ''} ${c.last_name || ''}`.trim() || 
                'Unknown User',
          user_id: c.id,
          role: (c as any).role || 'Collaborator', 
          is_portal_user: true,
          approval_status: 'approved' as const, 
          joined_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          profile_pic: c.profile_pic_signed || (c as any).user?.profile_pic_signed,
          user: c
      }))]
    };
  };

  const handleInviteResponse = async (action: 'accept' | 'reject', message: string) => {
    if (!reviewingCollaboration?.data) return;
    
    const data = reviewingCollaboration.data as CollaborationInviteData;
    
    await dispatch(respondToCollaborationInvite({
      notification_id: reviewingCollaboration.id,
      collaboration_id: data.collaboration_id,
      collaborator_id: data.collaborator_id,
      action: action,
      message: message || `Invitation ${action}ed via notification`,
    })).unwrap();

    setReviewingCollaboration(null);
    dispatch(fetchNotifications({ limit: 20, offset: 0 }));
  };

  const renderNotification = (notification: Notification) => {
    const IconComponent = getNotificationIcon(notification.type);
    const iconColorClass = getNotificationColor(notification.type);
    const senderProfilePic = notification.data?.sender_profile_pic;
    
    return (
      <motion.div
        key={notification.id}
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`relative p-4 rounded-2xl border transition-all duration-200 hover:shadow-md ${
                    notification.is_read 
                      ? 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700' 
                      : 'bg-blue-50/30 dark:bg-blue-900/10 border-blue-100 dark:border-blue-800/50'
                  }`}
        onClick={() => {
            handleNotificationClick(notification)
        }}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            {senderProfilePic ? (
               <Avatar 
                 src={senderProfilePic} 
                 alt={notification.data?.creator_name || notification.title || 'Sender'} 
                 size="md"
                 className="border border-gray-200 dark:border-gray-700"
               />
            ) : (
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconColorClass}`}>
                <IconComponent className="w-5 h-5" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className={`text-sm font-medium ${notification.is_read ? 'text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}`}>
                  {notification.title}
                  {(notification.count || 0) > 1 && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400">
                      {notification.count} new
                    </span>
                  )}
                </h3>
                <p className={`text-sm mt-1 ${notification.is_read ? 'text-gray-600 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  {notification.message}
                </p>
                
                {/* Special handling for collaboration invites */}
                {notification.type === 'collaboration_invite' && notification.requires_action && !notification.responded_at && (
                  <div className="mt-3 flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900/20 text-teal-800 dark:text-teal-400">
                      Action Required
                    </span>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        // e.stopPropagation();
                        setReviewingCollaboration(notification);
                      }}
                    >
                      Review Invitation
                    </Button>
                  </div>
                )}
                
                {notification.responded_at && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400">
                      ✓ Responded
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex-shrink-0 ml-4">
                {!notification.is_read && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1"></div>
                )}
              </div>
              
              {/* Timestamp - Absolute Bottom Right */}
              <span className="absolute bottom-3 right-4 text-[10px] font-medium text-gray-400 dark:text-gray-500">
                {formatShortTime(notification.created_at)}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="w-full mx-auto p-6  h-full overflow-y-auto px-6 scrollbar-hide">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Notifications</h1>
          {visibleUnreadCount > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              You have {visibleUnreadCount} unread notification{visibleUnreadCount !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        
          <div className="flex items-center justify-between md:justify-end gap-3 w-full md:w-auto">
            <div className="hidden md:flex items-center space-x-2 relative z-10">
              <Switch
                checked={isPushEnabled}
                onChange={async (checked) => {
                  // console.log('Switch toggled:', checked);
                  const { pushNotificationService } = await import('../services/pushNotificationService');
                  if (checked) {
                    // console.log('Requesting permission...');
                    const permission = await pushNotificationService.requestPermission();
                    // console.log('Permission:', permission);
                    if (permission === 'granted') {
                      // console.log('Subscribing...');
                      const success = await pushNotificationService.subscribe();
                      // console.log('Subscribe result:', success);
                      if (success) setIsPushEnabled(true);
                      else console.error('Subscription failed');
                    } else {
                        console.warn('Permission denied');
                    }
                  } else {
                    await pushNotificationService.unsubscribe();
                    setIsPushEnabled(false);
                  }
                }}
                label={isPushEnabled ? "Push Enabled" : "Enable Push"}
                size="sm"
              />
            </div>
          <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                filter === 'all'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                filter === 'unread'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Unread ({visibleUnreadCount})
            </button>
          </div>
          
          { (visibleUnreadCount > 0 || unreadCount > 0) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => dispatch(markAllAsRead())}
              className="flex items-center space-x-2 whitespace-nowrap"
            >
              <CheckIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Mark all read</span>
              <span className="sm:hidden">Mark read</span>
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <AnimatePresence mode="popLayout">
        {loading ? (
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="p-4 border border-gray-100 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredNotifications.length > 0 ? (
          <div className="space-y-4">
            {filteredNotifications.map(renderNotification)}
          </div>
        ) : (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
            <BellIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No notifications</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {filter === 'unread' 
                ? 'All caught up! Check back later for new updates.' 
                : 'When you get notifications, they\'ll show up here.'
              }
            </p>
          </div>
        )}
      </AnimatePresence>

      {/* Collaboration Review Modal (Reusing Showcase Modal) */}
      {reviewingCollaboration && reviewingCollaboration.data && (
        <div className="relative z-[100]">
            <CollaborationShowcaseModal
            collaboration={mapInviteToCollaboration(reviewingCollaboration)}
            onClose={() => setReviewingCollaboration(null)}
            onRespond={handleInviteResponse}
            />
        </div>
      )}

      {/* Pending CRM Lead Confirmation Modal */}
      {pendingLeadRequest && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl border border-gray-105 dark:border-gray-800">
            <div className="p-6 md:p-8 flex flex-col gap-6">
              
              {/* Creator details */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-2xl bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center font-bold text-xl text-teal-600 dark:text-teal-400">
                    {pendingLeadRequest.creator?.first_name?.charAt(0) || 'C'}
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-900 dark:text-white">CRM Collaboration Request</h3>
                    <p className="text-xs text-gray-500">From {pendingLeadRequest.creator?.first_name} {pendingLeadRequest.creator?.last_name}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setPendingLeadRequest(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>              {/* Status Alert if not pending */}
              {pendingLeadRequest.lead?.status !== 'pending_confirmation' && (
                <div className={`p-4 rounded-2xl border text-center text-sm font-bold flex items-center justify-center space-x-2 ${
                  pendingLeadRequest.lead?.status === 'confirmed' || pendingLeadRequest.lead?.status === 'accepted'
                    ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-450'
                    : 'bg-rose-50 dark:bg-rose-950/20 border-rose-100 dark:border-rose-900/50 text-rose-700 dark:text-rose-450'
                }`}>
                  {pendingLeadRequest.lead?.status === 'confirmed' || pendingLeadRequest.lead?.status === 'accepted' ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5 flex-shrink-0 text-emerald-600" />
                      <span>This request has already been Accepted / Confirmed.</span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="w-5 h-5 flex-shrink-0 text-rose-600" />
                      <span>This request has already been Declined.</span>
                    </>
                  )}
                </div>
              )}

              {/* Inquiry Details */}
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-2xl border border-gray-100 dark:border-gray-800 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400 font-medium">Interest Category</p>
                      <p className="font-bold text-gray-900 dark:text-white capitalize mt-0.5">{pendingLeadRequest.lead?.interest_type || 'General Shoot'}</p>
                    </div>
                    {pendingLeadRequest.lead?.estimated_budget > 0 && (
                      <div>
                        <p className="text-gray-400 font-medium">Estimated Budget</p>
                        <p className="font-bold text-teal-600 dark:text-teal-400 mt-0.5">₹{pendingLeadRequest.lead?.estimated_budget.toLocaleString('en-IN')}</p>
                      </div>
                    )}
                  </div>
                  
                  {pendingLeadRequest.lead?.expected_shoot_at && (
                    <div className="text-sm pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
                      <p className="text-gray-400 font-medium">Expected Date</p>
                      <p className="font-bold text-gray-900 dark:text-white mt-0.5">
                        {new Date(pendingLeadRequest.lead.expected_shoot_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}

                  {pendingLeadRequest.lead?.notes && (
                    <div className="text-sm pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
                      <p className="text-gray-400 font-medium">Message / Notes</p>
                      <p className="text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-line text-xs font-medium leading-relaxed">
                        {pendingLeadRequest.lead.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {pendingLeadRequest.lead?.status === 'pending_confirmation' ? (
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    onClick={() => handleConfirmLead(pendingLeadRequest.id, pendingLeadRequest.notificationId)}
                    className="flex-1 py-3 px-4 rounded-2xl bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm shadow-lg shadow-teal-600/10 transition-transform active:scale-95 flex items-center justify-center space-x-2"
                  >
                    <CheckCircleIcon className="w-5 h-5" />
                    <span>Confirm Request</span>
                  </button>
                  <button
                    onClick={() => handleDeclineLead(pendingLeadRequest.id, pendingLeadRequest.notificationId)}
                    className="flex-1 py-3 px-4 rounded-2xl bg-rose-50 dark:bg-rose-950/20 hover:bg-rose-100 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-455 font-bold text-sm transition-transform active:scale-95 flex items-center justify-center space-x-2 border border-rose-100 dark:border-rose-900/50"
                  >
                    <XCircleIcon className="w-5 h-5" />
                    <span>Decline Request</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setPendingLeadRequest(null)}
                  className="w-full py-3 px-4 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750 dark:text-gray-300 font-bold text-sm transition-transform active:scale-95 flex items-center justify-center cursor-pointer"
                >
                  Close View
                </button>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
